import '@Globals/Sentry'
import '@Globals/Stylus/trendhim-reset.styl'

import fromPairs from 'lodash/fromPairs'
import includes from 'lodash/includes'
import map from 'lodash/map'
import noop from 'lodash/noop'
import trimStart from 'lodash/trimStart'
import App, {
  AppContext,
  AppInitialProps,
  AppProps,
} from 'next/app'
import Head from 'next/head'
import React from 'react'

import { Event } from '@Events/Base'
import { FireQueuedEvents } from '@Events/Manager'
import { AnalyticsScript, SetupGA } from '@Globals/Google/Analytics'
import { DataLayer } from '@Globals/Google/DataLayer'
import { GTagScript } from '@Globals/Google/GTag'
import {
  AvailableLocale,
  CMSData,
  InfoData,
  MetadataProvider,
  PaymentIcons,
} from '@Globals/Metadata'
import { setPerformanceMetric } from '@Globals/Performance'
import { Name } from '@Globals/Resource'
import { SearchProvider } from '@Globals/Search/Context'
import { SplitTests } from '@Globals/SplitTests'
import { SplitTestsProvider } from '@Globals/SplitTests/Context'
import { Group } from '@Globals/SplitTests/Group'
import { TimeUsedOnPage } from '@Globals/TimeUsedOnPage'
import { UserProvider } from '@Globals/User'
import { Category } from '@Protos/catalog/catalog_pb'
import { Store } from '@Protos/stores/stores_pb'
import i18next from '@Server/i18n'

interface MyAppProps extends AppProps {
  activateSplitTest: (test: string, group: Group) => void
  addSurrogateKey: (key: string) => void
  availableLocales: AvailableLocale[]
  categories: Category.AsObject[]
  cmsData: CMSData
  err: any
  infoData: InfoData
  paymentIcons: PaymentIcons
  splitTests: SplitTests
  store: Store.AsObject
}

interface MyAppInitialProps extends AppInitialProps {
  activateSplitTest: (test: string, group: Group) => void
  addSurrogateKey: (key: string) => void
  availableLocales: AvailableLocale[]
  categories: Category.AsObject[]
  cmsData: CMSData
  infoData: InfoData
  paymentIcons: PaymentIcons
  splitTests: SplitTests
  store: Store.AsObject
}

const og = {
  'og:image': 'https://res.cloudinary.com/trendhim/image/upload/f_auto,c_pad,q_auto,w_1600,h_900/frontend/trendhim-default-share-2.jpg',
  'og:site_name': 'Trendhim',
  'og:type': 'article',
}

const MyApp = (
  {
    Component,
    activateSplitTest,
    addSurrogateKey,
    availableLocales,
    categories,
    cmsData,
    err,
    infoData,
    pageProps,
    paymentIcons,
    splitTests,
    store,
  }: MyAppProps,
) => {
  const selectFont = () => {
    const extendedLatinCountries = ['PL', 'CZ', 'SK', 'HU', 'RO']

    const countryCode = store?.config?.countryCode
    if (includes(extendedLatinCountries, countryCode)) {
      return 'gotham-latin-extended'
    }
    if (countryCode === 'GR') {
      return 'gotham-greek'
    }
    if (countryCode === 'BG') {
      return 'gotham-cyrillic'
    }
    return 'gotham-basic'
  }

  Event.setSplitTests(splitTests)
  Event.setStore(store)

  if (pageProps?.page?.type) {
    Event.setPage(pageProps.page.type, pageProps?.page?.id)
  }

  return (
    <SplitTestsProvider
      value={{
        activateSplitTest: activateSplitTest || noop,
        splitTests,
      }}
    >
      <Head>
        <meta
          content="width=device-width,initial-scale=1,maximum-scale=1,user-scalable=0"
          name="viewport"
        />
        {map(og, (value, key) => <meta key={key} content={value} property={key} />)}
        <link
          href={`/static/fonts/${selectFont()}.css`}
          rel="stylesheet"
        />
      </Head>
      <DataLayer
        input={{
          country: store?.config?.countryCode,
          currencyRates: fromPairs(store?.config?.adwordsCurrencyRatesMap),
          localCurrencyKey: store?.config?.currencyCode,
          storeId: new Name(store?.name).id,
        }}
      />
      <GTagScript measurementID={`AW-${store?.config?.adwordsTrackingId}`} />
      <AnalyticsScript />
      <MetadataProvider
        addSurrogateKey={addSurrogateKey || noop}
        availableLocales={availableLocales}
        categories={categories}
        cmsData={cmsData}
        infoData={infoData}
        page={pageProps?.page}
        paymentIcons={paymentIcons}
        store={store}
      >
        <SetupGA />
        <UserProvider>
          <SearchProvider>
            <Component {...pageProps} err={err} />
            <TimeUsedOnPage />
          </SearchProvider>
        </UserProvider>
        <FireQueuedEvents />
      </MetadataProvider>

      {store?.config?.trustpilotBusinessId && store?.config?.trustpilotDomain && (
        <script
          async
          src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
          type="text/javascript"
        />
      )}
    </SplitTestsProvider>
  )
}

MyApp.getInitialProps = async (appContext: AppContext): Promise<MyAppInitialProps> => {
  const appProps = await App.getInitialProps(appContext)
  const { defaultProps } = appContext.Component

  const addSurrogateKey = appContext?.ctx?.res?.thAddSurrogateKey || noop

  const availableLocales = appContext?.ctx?.req?.thAvailableLocales || []
  const categories = appContext?.ctx?.req?.thCategories || []
  const cmsData = appContext?.ctx?.req?.thCMSData || {
    latestArticles: [],
    latestCollections: [],
    urls: {
      aboutUs: '',
      cookiePolicy: '',
      privacyPolicy: '',
      terms: '',
    },
  }
  const infoData = appContext?.ctx?.req?.thInfoData || {
    urls: {
      delivery: '',
      paymentAndSecurity: '',
      returns: '',
    },
  }
  const paymentIcons = appContext?.ctx?.req?.thPaymentIcons || []
  const splitTests = appContext?.ctx?.req?.thSplitTests || {}
  const store = appContext?.ctx?.req?.thStore || new Store()

  return {
    ...appProps,
    activateSplitTest: (test: string, group: Group) => {
      console.log(`[split-test] activating ${test} ${group}`)

      const res = appContext?.ctx?.res
      if (!res) {
        return
      }

      const header = splitTests[test]?.header
      if (!header) {
        console.log(`[split-test] no header defined for ${test} ${group}`)
        return
      }

      res.thAddVary(header)
    },
    addSurrogateKey,
    availableLocales,
    categories: map(categories, (category) => {
      category.setSlug(trimStart(category.getSlug() || '', '/'))
      return category.toObject()
    }),
    cmsData,
    infoData,
    pageProps: {
      namespacesRequired: (defaultProps as any)?.i18nextNamespaces || [],
      page: {
        id: '',
        type: '',
      },
      ...((defaultProps as any)?.defaultPage || {}),
      ...(appProps.pageProps || {}),
    },
    paymentIcons,
    splitTests,
    store: store.toObject(),
  }
}

export function reportWebVitals(metric) {
  setPerformanceMetric(metric)
}

export default i18next.appWithTranslation(MyApp)
